import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

interface Stats {
  total_users: number;
  total_deposits: number;
  active_deposits: number;
  claimed_deposits: number;
  pending_withdrawals: number;
  pending_withdrawals_amount: number;
  total_balance: number;
}

interface User {
  id: number;
  wallet_address: string;
  balance: number;
  referral_bonus: number;
  referral_tier: string;
  deposits_count: number;
  referrals_count: number;
  created_at: string;
}

interface TierHistory {
  id: number;
  old_tier: string;
  new_tier: string;
  changed_by: string;
  changed_at: string;
  comment: string | null;
}

interface Withdrawal {
  id: number;
  user_id: number;
  user_wallet: string;
  ton_address: string;
  amount: number;
  fee: number;
  amount_to_receive: number;
  status: string;
  created_at: string;
  processed_at: string | null;
  processed_by: string | null;
  comment: string | null;
  transaction_hash: string | null;
}

interface PendingDeposit {
  id: number;
  user_id: number;
  telegram_user_id: string;
  user_wallet: string;
  amount: number;
  status: string;
  created_at: string;
  processed_at: string | null;
  processed_by: string | null;
  comment: string | null;
  transaction_hash: string | null;
}

interface Stake {
  id: number;
  user_id: number;
  wallet_address: string;
  amount: number;
  daily_rate: number;
  start_date: string;
  unlock_date: string;
  days_remaining: number;
  pending_rewards: number;
  status: string;
  last_claim_date: string | null;
  completed_at: string | null;
}

const AdminScreen = () => {
  const [activeTab, setActiveTab] = useState<'stats' | 'users' | 'withdrawals' | 'deposits' | 'stakes'>('stats');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [telegramId, setTelegramId] = useState<string>('demo');

  // Stats state
  const [stats, setStats] = useState<Stats | null>(null);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);

  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [withdrawalsTotal, setWithdrawalsTotal] = useState(0);
  const [withdrawalFilter, setWithdrawalFilter] = useState<string>('pending');

  // Deposits state
  const [deposits, setDeposits] = useState<PendingDeposit[]>([]);
  const [depositsTotal, setDepositsTotal] = useState(0);
  const [depositFilter, setDepositFilter] = useState<string>('pending');

  // Stakes state
  const [stakes, setStakes] = useState<Stake[]>([]);

  // Modal state
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [selectedDeposit, setSelectedDeposit] = useState<PendingDeposit | null>(null);
  const [modalComment, setModalComment] = useState('');
  const [modalTxHash, setModalTxHash] = useState('');

  // Tier management state
  const [selectedUserForTier, setSelectedUserForTier] = useState<User | null>(null);
  const [tierHistory, setTierHistory] = useState<TierHistory[]>([]);
  const [showTierModal, setShowTierModal] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'stats') loadStats();
      if (activeTab === 'users') loadUsers();
      if (activeTab === 'withdrawals') loadWithdrawals();
      if (activeTab === 'deposits') loadDeposits();
      if (activeTab === 'stakes') loadStakes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, activeTab, withdrawalFilter, depositFilter]);

  const checkAdminAccess = async () => {
    try {
      // Получаем Telegram ID из WebApp
      const tgId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
      setTelegramId(tgId);

      const response = await axios.post(`${API_URL}/admin/check`, {
        telegram_id: tgId
      });

      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('Admin check failed:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        params: { telegram_id: telegramId }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        params: { telegram_id: telegramId, limit: 100 }
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadWithdrawals = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/withdrawals`, {
        params: { 
          telegram_id: telegramId,
          status: withdrawalFilter === 'all' ? undefined : withdrawalFilter,
          limit: 100 
        }
      });
      setWithdrawals(response.data.withdrawals);
      setWithdrawalsTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load withdrawals:', error);
    }
  };

  const loadDeposits = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/pending-deposits`, {
        params: { 
          telegram_id: telegramId,
          status: depositFilter === 'all' ? undefined : depositFilter,
          limit: 100 
        }
      });
      setDeposits(response.data.deposits);
      setDepositsTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load deposits:', error);
    }
  };

  const loadStakes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stakes`, {
        params: { telegram_id: telegramId }
      });
      setStakes(response.data.stakes || []);
    } catch (error) {
      console.error('Failed to load stakes:', error);
    }
  };

  const handleWithdrawalAction = async (withdrawalId: number, status: 'approved' | 'rejected' | 'completed') => {
    try {
      await axios.post(`${API_URL}/admin/withdrawal/${withdrawalId}/update`, {
        status,
        comment: modalComment || undefined,
        transaction_hash: modalTxHash || undefined
      }, {
        params: { telegram_id: telegramId }
      });

      alert(`Заявка ${status === 'approved' ? 'одобрена' : status === 'rejected' ? 'отклонена' : 'завершена'}!`);
      setSelectedWithdrawal(null);
      setModalComment('');
      setModalTxHash('');
      loadWithdrawals();
    } catch (error: any) {
      console.error('Failed to update withdrawal:', error);
      alert('Ошибка: ' + (error.response?.data?.detail || 'Не удалось обновить заявку'));
    }
  };

  const handleDepositAction = async (depositId: number, status: 'confirmed' | 'rejected') => {
    try {
      await axios.post(`${API_URL}/admin/pending-deposit/${depositId}/update`, {
        status,
        comment: modalComment || undefined,
        transaction_hash: modalTxHash || undefined
      }, {
        params: { telegram_id: telegramId }
      });

      alert(`Депозит ${status === 'confirmed' ? 'подтверждён' : 'отклонён'}!`);
      setSelectedDeposit(null);
      setModalComment('');
      setModalTxHash('');
      loadDeposits();
      // Обновляем статистику
      loadStats();
    } catch (error: any) {
      console.error('Failed to update deposit:', error);
      alert('Ошибка: ' + (error.response?.data?.detail || 'Не удалось обновить заявку'));
    }
  };

  const setUserTier = async (userId: number, newTier: string) => {
    try {
      const response = await axios.post(`${API_URL}/admin/set-tier`, {
        admin_telegram_id: telegramId,
        user_id: userId,
        new_tier: newTier,
        comment: `Tier changed via admin panel`
      });

      alert(`✅ Tier изменен на ${newTier}!\nПроценты: ${response.data.percentages.level1}% / ${response.data.percentages.level2}% / ${response.data.percentages.level3}%`);
      
      // Обновляем tier конкретного пользователя в списке
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId 
            ? { ...u, referral_tier: newTier }
            : u
        )
      );
      
      // Если открыто модальное окно, обновляем selectedUserForTier
      if (selectedUserForTier && selectedUserForTier.id === userId) {
        setSelectedUserForTier(prev => 
          prev ? { ...prev, referral_tier: newTier } : null
        );
      }
    } catch (error: any) {
      console.error('Failed to set tier:', error);
      alert('Ошибка: ' + (error.response?.data?.detail || 'Не удалось изменить tier'));
    }
  };

  const loadTierHistory = async (userId: number) => {
    try {
      const response = await axios.get(`${API_URL}/admin/tier-history/${userId}`, {
        params: { telegram_id: telegramId }
      });
      setTierHistory(response.data);
    } catch (error) {
      console.error('Failed to load tier history:', error);
      setTierHistory([]);
    }
  };

  const openTierModal = (user: User) => {
    setSelectedUserForTier(user);
    loadTierHistory(user.id);
    setShowTierModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-white text-xl">Загрузка...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="text-white text-xl text-center p-6">
          <div className="text-6xl mb-4">🚫</div>
          <div className="text-2xl font-bold mb-2">Доступ запрещен</div>
          <div className="text-gray-300">У вас нет прав администратора</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white pb-20 px-3">
      <div className="max-w-6xl mx-auto pt-6">
        <h1 className="text-3xl font-bold mb-6">👨‍💼 Админ-панель</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              activeTab === 'stats'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            📊 Статистика
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            👥 Пользователи
          </button>
          <button
            onClick={() => setActiveTab('withdrawals')}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              activeTab === 'withdrawals'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            💰 Заявки на вывод
          </button>
          <button
            onClick={() => setActiveTab('deposits')}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              activeTab === 'deposits'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            💵 Пополнения
          </button>
          <button
            onClick={() => setActiveTab('stakes')}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              activeTab === 'stakes'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            💎 Stakes
          </button>
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl mb-2">👥</div>
              <div className="text-2xl font-bold">{stats.total_users}</div>
              <div className="text-sm text-white/70">Всего пользователей</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl mb-2">💎</div>
              <div className="text-2xl font-bold">{stats.total_deposits}</div>
              <div className="text-sm text-white/70">Всего депозитов</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl mb-2">✅</div>
              <div className="text-2xl font-bold">{stats.active_deposits}</div>
              <div className="text-sm text-white/70">Активных депозитов</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl mb-2">🎁</div>
              <div className="text-2xl font-bold">{stats.claimed_deposits}</div>
              <div className="text-sm text-white/70">Собрано наград</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl mb-2">⏳</div>
              <div className="text-2xl font-bold">{stats.pending_withdrawals}</div>
              <div className="text-sm text-white/70">Заявок на вывод</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl mb-2">💰</div>
              <div className="text-2xl font-bold">{stats.pending_withdrawals_amount.toFixed(2)}</div>
              <div className="text-sm text-white/70">TON к выводу</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 md:col-span-2 lg:col-span-3">
              <div className="text-4xl mb-2">💵</div>
              <div className="text-3xl font-bold">{stats.total_balance.toFixed(2)} TON</div>
              <div className="text-sm text-white/70">Общий баланс пользователей</div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
              <div className="text-lg font-semibold">Всего пользователей: {usersTotal}</div>
            </div>

            <div className="space-y-2">
              {users.map((user) => (
                <div key={user.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-mono text-sm text-blue-300">
                        {user.wallet_address}
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                          <span className="text-white/70">Баланс:</span>
                          <span className="ml-2 font-semibold">{user.balance.toFixed(2)} TON</span>
                        </div>
                        <div>
                          <span className="text-white/70">Реф. бонус:</span>
                          <span className="ml-2 font-semibold">{user.referral_bonus.toFixed(2)} TON</span>
                        </div>
                        <div>
                          <span className="text-white/70">Депозиты:</span>
                          <span className="ml-2 font-semibold">{user.deposits_count}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Рефералы:</span>
                          <span className="ml-2 font-semibold">{user.referrals_count}</span>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-white/50">
                        Регистрация: {new Date(user.created_at).toLocaleString('ru-RU')}
                      </div>
                      
                      {/* Tier Management */}
                      <div className="mt-4 pt-3 border-t border-white/20">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <span className="text-white/70 text-sm">Реферальный статус:</span>
                            <span className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${
                              user.referral_tier === 'vip' ? 'bg-yellow-500/30 text-yellow-200' :
                              user.referral_tier === 'partner' ? 'bg-blue-500/30 text-blue-200' :
                              'bg-gray-500/30 text-gray-200'
                            }`}>
                              {user.referral_tier === 'vip' ? '🌟 VIP Партнер' :
                               user.referral_tier === 'partner' ? '💎 Партнер' :
                               '👤 Стандарт'}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setUserTier(user.id, 'standard')}
                              disabled={user.referral_tier === 'standard'}
                              className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${
                                user.referral_tier === 'standard'
                                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-500 text-white hover:bg-gray-600'
                              }`}
                            >
                              Standard
                            </button>
                            <button
                              onClick={() => setUserTier(user.id, 'partner')}
                              disabled={user.referral_tier === 'partner'}
                              className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${
                                user.referral_tier === 'partner'
                                  ? 'bg-blue-600 text-blue-300 cursor-not-allowed'
                                  : 'bg-blue-500 text-white hover:bg-blue-600'
                              }`}
                            >
                              Partner
                            </button>
                            <button
                              onClick={() => setUserTier(user.id, 'vip')}
                              disabled={user.referral_tier === 'vip'}
                              className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${
                                user.referral_tier === 'vip'
                                  ? 'bg-yellow-600 text-yellow-300 cursor-not-allowed'
                                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
                              }`}
                            >
                              VIP
                            </button>
                            <button
                              onClick={() => openTierModal(user)}
                              className="px-3 py-1 rounded-lg text-sm font-semibold bg-purple-500 text-white hover:bg-purple-600 transition"
                            >
                              📜 История
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <div>
            {/* Filter */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
              <div className="flex gap-2 flex-wrap">
                {['all', 'pending', 'approved', 'rejected', 'completed'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setWithdrawalFilter(filter)}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${
                      withdrawalFilter === filter
                        ? 'bg-blue-600 text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {filter === 'all' && 'Все'}
                    {filter === 'pending' && '⏳ Ожидают'}
                    {filter === 'approved' && '✅ Одобрены'}
                    {filter === 'rejected' && '❌ Отклонены'}
                    {filter === 'completed' && '✔️ Завершены'}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-sm text-white/70">
                Всего заявок: {withdrawalsTotal}
              </div>
            </div>

            {/* Withdrawals List */}
            <div className="space-y-2">
              {withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-bold">#{withdrawal.id}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          withdrawal.status === 'pending' ? 'bg-yellow-600' :
                          withdrawal.status === 'approved' ? 'bg-green-600' :
                          withdrawal.status === 'rejected' ? 'bg-red-600' :
                          'bg-blue-600'
                        }`}>
                          {withdrawal.status === 'pending' && '⏳ Ожидает'}
                          {withdrawal.status === 'approved' && '✅ Одобрено'}
                          {withdrawal.status === 'rejected' && '❌ Отклонено'}
                          {withdrawal.status === 'completed' && '✔️ Завершено'}
                        </span>
                      </div>

                      <div className="mb-2">
                        <div className="text-2xl font-bold">{withdrawal.amount.toFixed(2)} TON</div>
                        <div className="text-sm text-red-400">- {(withdrawal.fee || 0).toFixed(2)} TON комиссия (7%)</div>
                        <div className="text-lg text-green-400 font-semibold">= {(withdrawal.amount_to_receive || withdrawal.amount).toFixed(2)} TON к выводу</div>
                      </div>

                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="text-white/70">Пользователь:</span>
                          <span className="ml-2 font-mono text-blue-300">{withdrawal.user_wallet}</span>
                        </div>
                        <div>
                          <span className="text-white/70">TON адрес:</span>
                          <span className="ml-2 font-mono text-green-300">{withdrawal.ton_address}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Создано:</span>
                          <span className="ml-2">{new Date(withdrawal.created_at).toLocaleString('ru-RU')}</span>
                        </div>
                        {withdrawal.processed_at && (
                          <div>
                            <span className="text-white/70">Обработано:</span>
                            <span className="ml-2">{new Date(withdrawal.processed_at).toLocaleString('ru-RU')}</span>
                          </div>
                        )}
                        {withdrawal.comment && (
                          <div>
                            <span className="text-white/70">Комментарий:</span>
                            <span className="ml-2 italic">{withdrawal.comment}</span>
                          </div>
                        )}
                        {withdrawal.transaction_hash && (
                          <div>
                            <span className="text-white/70">TX Hash:</span>
                            <span className="ml-2 font-mono text-xs">{withdrawal.transaction_hash}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {withdrawal.status === 'pending' && (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => setSelectedWithdrawal(withdrawal)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                      >
                        ✅ Одобрить
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Отклонить заявку? Средства вернутся пользователю.')) {
                            handleWithdrawalAction(withdrawal.id, 'rejected');
                          }
                        }}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                      >
                        ❌ Отклонить
                      </button>
                    </div>
                  )}

                  {withdrawal.status === 'approved' && (
                    <button
                      onClick={() => setSelectedWithdrawal(withdrawal)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition mt-4"
                    >
                      ✔️ Отметить как завершено
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deposits Tab */}
        {activeTab === 'deposits' && (
          <div>
            {/* Filter */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
              <div className="flex gap-2 flex-wrap">
                {['all', 'pending', 'confirmed', 'rejected'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setDepositFilter(filter)}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold transition ${
                      depositFilter === filter
                        ? 'bg-blue-600 text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {filter === 'all' && 'Все'}
                    {filter === 'pending' && '⏳ Ожидают'}
                    {filter === 'confirmed' && '✅ Подтверждены'}
                    {filter === 'rejected' && '❌ Отклонены'}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-sm text-white/70">
                Всего депозитов: {depositsTotal}
              </div>
            </div>

            {/* Deposits List */}
            <div className="space-y-2">
              {deposits.map((deposit) => (
                <div key={deposit.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-bold">#{deposit.id}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          deposit.status === 'pending' ? 'bg-yellow-600' :
                          deposit.status === 'confirmed' ? 'bg-green-600' :
                          'bg-red-600'
                        }`}>
                          {deposit.status === 'pending' && '⏳ Ожидает'}
                          {deposit.status === 'confirmed' && '✅ Подтверждён'}
                          {deposit.status === 'rejected' && '❌ Отклонён'}
                        </span>
                      </div>

                      <div className="text-2xl font-bold mb-2 text-green-400">
                        {deposit.amount.toFixed(2)} TON
                      </div>

                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="text-white/70">User ID:</span>
                          <span className="ml-2 font-mono text-blue-300">{deposit.telegram_user_id}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Кошелёк:</span>
                          <span className="ml-2 font-mono text-blue-300 text-xs">{deposit.user_wallet}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Создано:</span>
                          <span className="ml-2">{new Date(deposit.created_at).toLocaleString('ru-RU')}</span>
                        </div>
                        {deposit.processed_at && (
                          <div>
                            <span className="text-white/70">Обработано:</span>
                            <span className="ml-2">{new Date(deposit.processed_at).toLocaleString('ru-RU')}</span>
                          </div>
                        )}
                        {deposit.comment && (
                          <div>
                            <span className="text-white/70">Комментарий:</span>
                            <span className="ml-2 italic">{deposit.comment}</span>
                          </div>
                        )}
                        {deposit.transaction_hash && (
                          <div>
                            <span className="text-white/70">TX Hash:</span>
                            <span className="ml-2 font-mono text-xs break-all">{deposit.transaction_hash}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {deposit.status === 'pending' && (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => setSelectedDeposit(deposit)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                      >
                        ✅ Подтвердить
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Отклонить депозит?')) {
                            setSelectedDeposit(deposit);
                            setModalComment('Депозит отклонён');
                            handleDepositAction(deposit.id, 'rejected');
                          }
                        }}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                      >
                        ❌ Отклонить
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stakes Tab */}
        {activeTab === 'stakes' && (
          <div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
              <div className="text-sm text-white/70">
                Всего stakes: {stakes.length}
              </div>
            </div>

            {/* Stakes List */}
            <div className="space-y-2">
              {stakes.map((stake) => (
                <div key={stake.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-bold">#{stake.id}</span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          stake.status === 'active' ? 'bg-green-600' : 'bg-gray-600'
                        }`}>
                          {stake.status === 'active' ? '🔒 Активен' : '✅ Завершён'}
                        </span>
                        {stake.days_remaining === 0 && stake.status === 'active' && (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-600">
                            🎉 Разблокирован
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <div className="text-sm text-white/70">Сумма стейка:</div>
                          <div className="text-xl font-bold text-purple-400">{stake.amount} TON</div>
                        </div>
                        <div>
                          <div className="text-sm text-white/70">Накоплено наград:</div>
                          <div className="text-xl font-bold text-green-400">
                            +{stake.pending_rewards.toFixed(6)} TON
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="text-white/70">User ID:</span>
                          <span className="ml-2 font-mono text-blue-300">{stake.user_id}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Кошелёк:</span>
                          <span className="ml-2 font-mono text-blue-300 text-xs">{stake.wallet_address}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Дневная ставка:</span>
                          <span className="ml-2 text-green-400 font-semibold">{stake.daily_rate}%</span>
                        </div>
                        <div>
                          <span className="text-white/70">Начало:</span>
                          <span className="ml-2">{new Date(stake.start_date).toLocaleString('ru-RU')}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Разблокировка:</span>
                          <span className="ml-2">{new Date(stake.unlock_date).toLocaleString('ru-RU')}</span>
                        </div>
                        <div>
                          <span className="text-white/70">Дней до разблокировки:</span>
                          <span className="ml-2 font-bold">
                            {stake.days_remaining > 0 ? `${stake.days_remaining} дн.` : '✅ Готов!'}
                          </span>
                        </div>
                        {stake.last_claim_date && (
                          <div>
                            <span className="text-white/70">Последний claim:</span>
                            <span className="ml-2">{new Date(stake.last_claim_date).toLocaleString('ru-RU')}</span>
                          </div>
                        )}
                        {stake.completed_at && (
                          <div>
                            <span className="text-white/70">Завершён:</span>
                            <span className="ml-2">{new Date(stake.completed_at).toLocaleString('ru-RU')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {stakes.length === 0 && (
                <div className="text-center py-12 text-white/50">
                  Нет активных stakes
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal for deposit confirmation */}
        {selectedDeposit && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-slate-900 rounded-lg p-6 max-w-lg w-full">
              <h3 className="text-xl font-bold mb-4">Подтвердить депозит</h3>

              <div className="mb-4">
                <div className="text-2xl font-bold text-green-400 mb-2">{selectedDeposit.amount} TON</div>
                <div className="text-sm text-white/70">
                  User ID: {selectedDeposit.telegram_user_id}
                </div>
                <div className="text-sm text-white/70 font-mono break-all text-xs">
                  Кошелёк: {selectedDeposit.user_wallet}
                </div>
              </div>

              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4 text-sm">
                <div className="font-semibold mb-1">⚠️ Перед подтверждением:</div>
                <div className="text-white/90">
                  1. Проверь входящий платёж в Tonkeeper<br/>
                  2. Убедись что сумма совпадает ({selectedDeposit.amount} TON)<br/>
                  3. Проверь User ID в комментарии транзакции
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Transaction Hash (опционально)
                  </label>
                  <input
                    type="text"
                    value={modalTxHash}
                    onChange={(e) => setModalTxHash(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white font-mono text-sm"
                    placeholder="Хеш транзакции из Tonkeeper..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Комментарий (опционально)
                  </label>
                  <textarea
                    value={modalComment}
                    onChange={(e) => setModalComment(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white"
                    rows={2}
                    placeholder="Комментарий для истории..."
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleDepositAction(selectedDeposit.id, 'confirmed')}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  ✅ Подтвердить
                </button>
                <button
                  onClick={() => {
                    setSelectedDeposit(null);
                    setModalComment('');
                    setModalTxHash('');
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal for withdrawal action */}
        {selectedWithdrawal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-slate-900 rounded-lg p-6 max-w-lg w-full">
              <h3 className="text-xl font-bold mb-4">
                {selectedWithdrawal.status === 'pending' ? 'Одобрить заявку' : 'Завершить заявку'}
              </h3>

              <div className="mb-4">
                <div className="space-y-1 mb-3">
                  <div className="text-xl font-bold">{selectedWithdrawal.amount.toFixed(2)} TON</div>
                  <div className="text-sm text-red-400">- {(selectedWithdrawal.fee || 0).toFixed(2)} TON комиссия (7%)</div>
                  <div className="text-lg text-green-400 font-semibold">= {(selectedWithdrawal.amount_to_receive || selectedWithdrawal.amount).toFixed(2)} TON отправить пользователю</div>
                </div>
                <div className="text-sm text-white/70">
                  Пользователь: {selectedWithdrawal.user_wallet}
                </div>
                <div className="text-sm text-white/70 font-mono break-all">
                  TON адрес: {selectedWithdrawal.ton_address}
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Комментарий (опционально)
                  </label>
                  <textarea
                    value={modalComment}
                    onChange={(e) => setModalComment(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white"
                    rows={2}
                    placeholder="Комментарий для пользователя..."
                  />
                </div>

                {selectedWithdrawal.status === 'approved' && (
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Transaction Hash
                    </label>
                    <input
                      type="text"
                      value={modalTxHash}
                      onChange={(e) => setModalTxHash(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg p-2 text-white font-mono text-sm"
                      placeholder="Hash транзакции в TON..."
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSelectedWithdrawal(null);
                    setModalComment('');
                    setModalTxHash('');
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  Отмена
                </button>
                <button
                  onClick={() => handleWithdrawalAction(
                    selectedWithdrawal.id,
                    selectedWithdrawal.status === 'pending' ? 'approved' : 'completed'
                  )}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                >
                  {selectedWithdrawal.status === 'pending' ? '✅ Одобрить' : '✔️ Завершить'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tier History Modal */}
        {showTierModal && selectedUserForTier && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-gradient-to-br from-purple-900 to-blue-900 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4 text-white">
                📜 История изменений tier
              </h2>
              
              <div className="mb-4 p-3 bg-white/10 rounded-lg">
                <div className="text-sm text-white/70">Пользователь:</div>
                <div className="font-mono text-blue-300">{selectedUserForTier.wallet_address}</div>
                <div className="text-sm mt-2">
                  <span className="text-white/70">Текущий tier:</span>
                  <span className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${
                    selectedUserForTier.referral_tier === 'vip' ? 'bg-yellow-500/30 text-yellow-200' :
                    selectedUserForTier.referral_tier === 'partner' ? 'bg-blue-500/30 text-blue-200' :
                    'bg-gray-500/30 text-gray-200'
                  }`}>
                    {selectedUserForTier.referral_tier === 'vip' ? '🌟 VIP Партнер' :
                     selectedUserForTier.referral_tier === 'partner' ? '💎 Партнер' :
                     '👤 Стандарт'}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {tierHistory.length === 0 ? (
                  <div className="text-center text-white/50 py-8">
                    История изменений пуста
                  </div>
                ) : (
                  tierHistory.map((entry) => (
                    <div key={entry.id} className="bg-white/10 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm">
                          <span className="text-white/70">Изменено:</span>
                          <span className="ml-2 text-white font-semibold">
                            {entry.old_tier} → {entry.new_tier}
                          </span>
                        </div>
                        <div className="text-xs text-white/50">
                          {new Date(entry.changed_at).toLocaleString('ru-RU')}
                        </div>
                      </div>
                      <div className="text-xs text-white/70">
                        <span>Изменил:</span>
                        <span className="ml-1 font-mono">{entry.changed_by}</span>
                      </div>
                      {entry.comment && (
                        <div className="text-xs text-white/60 mt-2 italic">
                          {entry.comment}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTierModal(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-6 rounded-lg transition"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminScreen;

